import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Button,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const TermsOfService = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const lastUpdated = 'May 18, 2026';
  const effectiveDate = 'February 16, 2026';

  const SectionTitle = ({ children }) => (
    <Typography
      variant="h5"
      component="h2"
      sx={{ mt: 4, mb: 2, color: theme.palette.primary.main }}
    >
      {children}
    </Typography>
  );

  const SubSectionTitle = ({ children }) => (
    <Typography
      variant="h6"
      component="h3"
      sx={{ mt: 3, mb: 1.5, color: theme.palette.text.primary }}
    >
      {children}
    </Typography>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Terms of Service
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            Last Updated: {lastUpdated} | Effective Date: {effectiveDate}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Introduction */}
          <Typography variant="body1" paragraph>
            Welcome to FamilyForLife! These Terms of Service ("Terms") govern your access
            to and use of the FamilyForLife web application and related services
            (collectively, the "Service"). By accessing or using the Service, you agree
            to be bound by these Terms.
          </Typography>

          <Typography variant="body1" paragraph sx={{ fontWeight: 600 }}>
            Please read these Terms carefully before using the Service. If you do not
            agree to these Terms, you may not access or use the Service.
          </Typography>

          {/* Table of Contents */}
          <SectionTitle>Table of Contents</SectionTitle>
          <List dense>
            {[
              'Acceptance of Terms',
              'Description of Service',
              'Account Registration',
              'SMS / Text Message Communications',
              'User Conduct',
              'Content and Intellectual Property',
              'Family Groups and Sharing',
              'Privacy',
              'Prohibited Uses',
              'Termination',
              'Disclaimers',
              'Limitation of Liability',
              'Indemnification',
              'Dispute Resolution',
              'Changes to Terms',
              'General Provisions',
              'Contact Us',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${index + 1}. ${item}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          {/* 1. Acceptance of Terms */}
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Typography variant="body1" paragraph>
            By creating an account or using the Service, you acknowledge that you have
            read, understood, and agree to be bound by these Terms, our{' '}
            <Link component={RouterLink} to="/privacy">Privacy Policy</Link>, and our{' '}
            <Link component={RouterLink} to="/cookies">Cookie Policy</Link>, which are
            incorporated into these Terms by reference.
          </Typography>
          <Typography variant="body1" paragraph>
            If you are using the Service on behalf of a family or organization, you
            represent that you have the authority to bind that family or organization
            to these Terms.
          </Typography>

          {/* 2. Description of Service */}
          <SectionTitle>2. Description of Service</SectionTitle>
          <Typography variant="body1" paragraph>
            FamilyForLife is a family management platform that enables users to:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Connect with family members and build family networks" /></ListItem>
            <ListItem><ListItemText primary="Share photos, documents, and important information" /></ListItem>
            <ListItem><ListItemText primary="Manage family events, announcements, and calendars" /></ListItem>
            <ListItem><ListItemText primary="Create and visualize family trees" /></ListItem>
            <ListItem><ListItemText primary="Store sensitive documents in secure family vaults" /></ListItem>
            <ListItem><ListItemText primary="Maintain health updates and emergency contact information" /></ListItem>
            <ListItem><ListItemText primary="Communicate through discussions and messaging" /></ListItem>
          </List>
          <Typography variant="body1" paragraph>
            We reserve the right to modify, suspend, or discontinue any part of the
            Service at any time, with or without notice.
          </Typography>

          {/* 3. Account Registration */}
          <SectionTitle>3. Account Registration</SectionTitle>

          <SubSectionTitle>3.1 Eligibility</SubSectionTitle>
          <Typography variant="body1" paragraph>
            To use the Service, you must be at least 13 years of age (or 16 in the
            European Economic Area). If you are under 18, you must have permission
            from a parent or guardian to use the Service.
          </Typography>

          <SubSectionTitle>3.2 Account Creation</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You agree to:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Provide accurate and complete information during registration" /></ListItem>
            <ListItem><ListItemText primary="Maintain the security of your account credentials" /></ListItem>
            <ListItem><ListItemText primary="Promptly update your information if it changes" /></ListItem>
            <ListItem><ListItemText primary="Notify us immediately of any unauthorized access to your account" /></ListItem>
          </List>

          <SubSectionTitle>3.3 Account Security</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You are responsible for all activities that occur under your account.
            We recommend using a strong, unique password and enabling any available
            security features. We are not liable for any loss or damage arising from
            unauthorized use of your account.
          </Typography>

          {/* 4. SMS / Text Message Communications */}
          <SectionTitle>4. SMS / Text Message Communications</SectionTitle>
          <Typography variant="body1" paragraph>
            By providing your mobile phone number and checking the consent box during account
            registration at familyforlife.app, you agree to receive text messages from
            Family For Life App Inc, including:
          </Typography>
          <List>
            <ListItem><ListItemText primary="One-time passcodes (OTP) for account verification" /></ListItem>
            <ListItem><ListItemText primary="Account notifications and security alerts" /></ListItem>
            <ListItem><ListItemText primary="Event reminders and RSVP prompts" /></ListItem>
            <ListItem><ListItemText primary="Family group updates" /></ListItem>
            <ListItem><ListItemText primary="Payment and billing reminders" /></ListItem>
          </List>

          <SubSectionTitle>4.1 Consent Language</SubSectionTitle>
          <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', pl: 2, borderLeft: `3px solid`, borderColor: 'primary.main' }}>
            "I agree to receive text messages from Family For Life App Inc, including
            verification codes, account notifications, and event reminders. Message and data
            rates may apply. Reply STOP to opt out at any time."
          </Typography>
          <Typography variant="body1" paragraph>
            You may also opt in at any time by texting <strong>START</strong> or <strong>JOIN</strong> to
            our campaign number.
          </Typography>

          <SubSectionTitle>4.2 Message Frequency &amp; Rates</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Message frequency varies based on your account activity. Standard message and data
            rates may apply depending on your mobile carrier and plan.
          </Typography>

          <SubSectionTitle>4.3 Opt-Out</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You may opt out of SMS messages at any time by replying <strong>STOP</strong>,{' '}
            <strong>CANCEL</strong>, <strong>END</strong>, <strong>QUIT</strong>,{' '}
            <strong>UNSUBSCRIBE</strong>, or <strong>REVOKE</strong> to any message we send.
            You will receive a final confirmation and no further messages will be sent.
            Reply <strong>START</strong> at any time to resubscribe.
          </Typography>

          <SubSectionTitle>4.4 Help</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Reply <strong>HELP</strong> or <strong>INFO</strong> for assistance, or contact us
            at <Link href="mailto:support@familyforlife.app">support@familyforlife.app</Link>.
          </Typography>

          <SubSectionTitle>4.5 SMS Provider</SubSectionTitle>
          <Typography variant="body1" paragraph>
            SMS messages are delivered via Twilio, Inc. Your mobile number is shared with
            Twilio solely to deliver messages you have requested. Mobile information is not
            shared with third parties or affiliates for marketing or promotional purposes.
            See our <Link component={RouterLink} to="/privacy-policy">Privacy Policy</Link> for
            full details.
          </Typography>

          {/* 5. User Conduct */}
          <SectionTitle>5. User Conduct</SectionTitle>
          <Typography variant="body1" paragraph>
            You agree to use the Service only for lawful purposes and in accordance
            with these Terms. You agree not to:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Violate any applicable laws, regulations, or third-party rights" /></ListItem>
            <ListItem><ListItemText primary="Upload or share content that is illegal, harmful, or objectionable" /></ListItem>
            <ListItem><ListItemText primary="Harass, abuse, or harm other users" /></ListItem>
            <ListItem><ListItemText primary="Impersonate any person or misrepresent your affiliation" /></ListItem>
            <ListItem><ListItemText primary="Interfere with or disrupt the Service or servers" /></ListItem>
            <ListItem><ListItemText primary="Attempt to gain unauthorized access to any part of the Service" /></ListItem>
            <ListItem><ListItemText primary="Use the Service for commercial purposes without our permission" /></ListItem>
            <ListItem><ListItemText primary="Collect or harvest user information without consent" /></ListItem>
          </List>

          {/* 6. Content and Intellectual Property */}
          <SectionTitle>6. Content and Intellectual Property</SectionTitle>

          <SubSectionTitle>6.1 Your Content</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You retain ownership of all content you upload, post, or share through the
            Service ("Your Content"). By uploading content, you grant us a limited,
            non-exclusive, royalty-free license to use, store, display, and distribute
            Your Content solely to provide and improve the Service.
          </Typography>

          <SubSectionTitle>6.2 Content Responsibility</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You are solely responsible for Your Content and represent that:
          </Typography>
          <List>
            <ListItem><ListItemText primary="You own or have the rights to share the content" /></ListItem>
            <ListItem><ListItemText primary="Your Content does not violate any third-party rights" /></ListItem>
            <ListItem><ListItemText primary="Your Content complies with applicable laws" /></ListItem>
          </List>

          <SubSectionTitle>6.3 Our Intellectual Property</SubSectionTitle>
          <Typography variant="body1" paragraph>
            The Service, including its design, features, code, and branding, is owned
            by FamilyForLife and protected by copyright, trademark, and other
            intellectual property laws. You may not copy, modify, distribute, or create
            derivative works without our written permission.
          </Typography>

          {/* 7. Family Groups and Sharing */}
          <SectionTitle>7. Family Groups and Sharing</SectionTitle>

          <SubSectionTitle>7.1 Creating and Managing Families</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Users may create family groups and invite other members to join. Family
            administrators have additional responsibilities for managing group settings,
            permissions, and member access.
          </Typography>

          <SubSectionTitle>7.2 Shared Content</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Content shared within a family group is visible to members based on
            permission settings. You acknowledge that other family members may view,
            download, or share content you post within the family group.
          </Typography>

          <SubSectionTitle>7.3 Family Administrator Responsibilities</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Family administrators agree to:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Manage family membership responsibly" /></ListItem>
            <ListItem><ListItemText primary="Ensure appropriate permissions are set" /></ListItem>
            <ListItem><ListItemText primary="Remove content or members that violate these Terms" /></ListItem>
            <ListItem><ListItemText primary="Handle disputes within the family group fairly" /></ListItem>
          </List>

          {/* 8. Privacy */}
          <SectionTitle>8. Privacy</SectionTitle>
          <Typography variant="body1" paragraph>
            Your privacy is important to us. Our{' '}
            <Link component={RouterLink} to="/privacy">Privacy Policy</Link> describes
            how we collect, use, and protect your personal information. By using the
            Service, you consent to our data practices as described in the Privacy Policy.
          </Typography>

          {/* 9. Prohibited Uses */}
          <SectionTitle>9. Prohibited Uses</SectionTitle>
          <Typography variant="body1" paragraph>
            The following uses of the Service are strictly prohibited:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Using the Service to store or distribute malware or viruses" /></ListItem>
            <ListItem><ListItemText primary="Engaging in phishing, spamming, or fraudulent activities" /></ListItem>
            <ListItem><ListItemText primary="Uploading content depicting child exploitation or abuse" /></ListItem>
            <ListItem><ListItemText primary="Using automated systems (bots, scrapers) without permission" /></ListItem>
            <ListItem><ListItemText primary="Circumventing security measures or access controls" /></ListItem>
            <ListItem><ListItemText primary="Reverse engineering or decompiling the Service" /></ListItem>
            <ListItem><ListItemText primary="Using the Service for illegal surveillance or stalking" /></ListItem>
            <ListItem><ListItemText primary="Reselling or commercializing the Service without authorization" /></ListItem>
          </List>
          <Typography variant="body1" paragraph>
            Violation of these prohibitions may result in immediate termination of your
            account and reporting to appropriate authorities.
          </Typography>

          {/* 10. Termination */}
          <SectionTitle>10. Termination</SectionTitle>

          <SubSectionTitle>10.1 Termination by You</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You may terminate your account at any time through your account settings
            or by contacting us. Upon termination, your account data will be handled
            according to our Privacy Policy.
          </Typography>

          <SubSectionTitle>10.2 Termination by Us</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We may suspend or terminate your account if:
          </Typography>
          <List>
            <ListItem><ListItemText primary="You violate these Terms" /></ListItem>
            <ListItem><ListItemText primary="We are required to do so by law" /></ListItem>
            <ListItem><ListItemText primary="We reasonably believe your conduct may harm other users" /></ListItem>
            <ListItem><ListItemText primary="Your account has been inactive for an extended period" /></ListItem>
          </List>

          <SubSectionTitle>10.3 Effect of Termination</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Upon termination, your right to access the Service ceases. Sections of
            these Terms that by their nature should survive termination will survive,
            including intellectual property provisions, disclaimers, and limitations
            of liability.
          </Typography>

          {/* 11. Disclaimers */}
          <SectionTitle>11. Disclaimers</SectionTitle>
          <Typography variant="body1" paragraph sx={{ fontWeight: 600 }}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
            OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING
            IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            AND NON-INFRINGEMENT.
          </Typography>
          <Typography variant="body1" paragraph>
            We do not warrant that:
          </Typography>
          <List>
            <ListItem><ListItemText primary="The Service will be uninterrupted or error-free" /></ListItem>
            <ListItem><ListItemText primary="Defects will be corrected" /></ListItem>
            <ListItem><ListItemText primary="The Service is free of viruses or harmful components" /></ListItem>
            <ListItem><ListItemText primary="The results of using the Service will meet your requirements" /></ListItem>
          </List>

          {/* 12. Limitation of Liability */}
          <SectionTitle>12. Limitation of Liability</SectionTitle>
          <Typography variant="body1" paragraph sx={{ fontWeight: 600 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, FAMILYFORLIFE AND ITS OFFICERS,
            DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS
            OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE.
          </Typography>
          <Typography variant="body1" paragraph>
            Our total liability for any claims arising from these Terms or your use
            of the Service shall not exceed the amount you paid us (if any) in the
            twelve months preceding the claim, or $100 USD, whichever is greater.
          </Typography>
          <Typography variant="body1" paragraph>
            Some jurisdictions do not allow limitations on implied warranties or
            liability for incidental or consequential damages. If these laws apply
            to you, some or all of the above limitations may not apply, and you may
            have additional rights.
          </Typography>

          {/* 13. Indemnification */}
          <SectionTitle>13. Indemnification</SectionTitle>
          <Typography variant="body1" paragraph>
            You agree to defend, indemnify, and hold harmless FamilyForLife and its
            affiliates, officers, directors, employees, and agents from any claims,
            damages, losses, liabilities, and expenses (including reasonable
            attorneys' fees) arising from:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Your use of the Service" /></ListItem>
            <ListItem><ListItemText primary="Your violation of these Terms" /></ListItem>
            <ListItem><ListItemText primary="Your violation of any third-party rights" /></ListItem>
            <ListItem><ListItemText primary="Your Content" /></ListItem>
          </List>

          {/* 14. Dispute Resolution */}
          <SectionTitle>14. Dispute Resolution</SectionTitle>

          <SubSectionTitle>14.1 Informal Resolution</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Before filing a formal dispute, you agree to try to resolve the issue
            informally by contacting us at{' '}
            <Link href="mailto:legal@familyforlife.app">legal@familyforlife.app</Link>.
            We will attempt to resolve disputes within 30 days.
          </Typography>

          <SubSectionTitle>14.2 Governing Law</SubSectionTitle>
          <Typography variant="body1" paragraph>
            These Terms are governed by and construed in accordance with the laws
            of the State of Delaware, United States, without regard to conflict of
            law principles.
          </Typography>

          <SubSectionTitle>14.3 Arbitration Agreement</SubSectionTitle>
          <Typography variant="body1" paragraph>
            For any dispute not resolved informally, you and FamilyForLife agree to
            resolve the dispute through binding arbitration administered by the
            American Arbitration Association (AAA) under its Consumer Arbitration
            Rules. You and FamilyForLife waive any right to a jury trial or to
            participate in a class action.
          </Typography>

          <SubSectionTitle>14.4 Exceptions</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Either party may bring claims in small claims court if eligible. Either
            party may seek injunctive relief in any court of competent jurisdiction
            for intellectual property violations or unauthorized access.
          </Typography>

          {/* 15. Changes to Terms */}
          <SectionTitle>15. Changes to Terms</SectionTitle>
          <Typography variant="body1" paragraph>
            We may update these Terms from time to time. We will notify you of
            material changes by posting the updated Terms and changing the "Last
            Updated" date. For significant changes, we may also provide additional
            notice, such as email notification or in-app alerts.
          </Typography>
          <Typography variant="body1" paragraph>
            Your continued use of the Service after changes become effective
            constitutes acceptance of the revised Terms. If you do not agree to
            the updated Terms, you must stop using the Service.
          </Typography>

          {/* 16. General Provisions */}
          <SectionTitle>16. General Provisions</SectionTitle>

          <SubSectionTitle>16.1 Entire Agreement</SubSectionTitle>
          <Typography variant="body1" paragraph>
            These Terms, together with our Privacy Policy and Cookie Policy,
            constitute the entire agreement between you and FamilyForLife regarding
            the Service.
          </Typography>

          <SubSectionTitle>16.2 Severability</SubSectionTitle>
          <Typography variant="body1" paragraph>
            If any provision of these Terms is found unenforceable, the remaining
            provisions will continue in full force and effect.
          </Typography>

          <SubSectionTitle>16.3 Waiver</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Our failure to enforce any right or provision of these Terms does not
            constitute a waiver of that right or provision.
          </Typography>

          <SubSectionTitle>16.4 Assignment</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You may not assign your rights or obligations under these Terms without
            our written consent. We may assign our rights and obligations without
            restriction.
          </Typography>

          <SubSectionTitle>16.5 Force Majeure</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We are not liable for any delay or failure to perform due to causes
            beyond our reasonable control, including natural disasters, war, terrorism,
            labor disputes, or governmental actions.
          </Typography>

          {/* 17. Contact Us */}
          <SectionTitle>17. Contact Us</SectionTitle>
          <Typography variant="body1" paragraph>
            If you have questions about these Terms, please contact us:
          </Typography>
          <Box sx={{ pl: 2, borderLeft: `3px solid ${theme.palette.primary.main}`, my: 2 }}>
            <Typography variant="body1">
              <strong>FamilyForLife</strong><br />
              Email: <Link href="mailto:legal@familyforlife.app">legal@familyforlife.app</Link><br />
              Support: <Link href="mailto:support@familyforlife.app">support@familyforlife.app</Link>
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/privacy" color="primary">
              Privacy Policy
            </Link>
            <Link component={RouterLink} to="/cookies" color="primary">
              Cookie Policy
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;
